import React, { useState, useEffect } from 'react';
import { TrendingUp, Eye, Heart, Clock, Play, Calendar, ArrowUpRight, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIcon, background: color + '22', color }}>
        <Icon size={18} />
      </div>
      <div>
        <p style={styles.statLabel}>{label}</p>
        <p style={styles.statValue}>{value}</p>
        {sub && <p style={styles.statSub}>{sub}</p>}
      </div>
    </div>
  );
}

function VideoRow({ video }) {
  const statusConfig = {
    scheduled: { label: 'Scheduled', color: 'var(--accent)', bg: 'var(--accent-light)' },
    published: { label: 'Published', color: 'var(--green)', bg: 'var(--green-light)' },
    draft: { label: 'Draft', color: 'var(--yellow)', bg: 'var(--yellow-light)' },
    failed: { label: 'Failed', color: 'var(--pink)', bg: 'var(--pink-light)' },
    publishing: { label: 'Publishing', color: 'var(--cyan)', bg: 'rgba(0,212,255,0.1)' },
  };
  const s = statusConfig[video.status] || statusConfig.draft;
  const scheduledAt = video.scheduled_at || video.scheduledAt;

  return (
    <div style={styles.videoRow}>
      <div style={styles.videoThumb}>
        <Play size={16} color="var(--text-muted)" />
      </div>
      <div style={styles.videoInfo}>
        <p style={styles.videoTitle}>{video.title}</p>
        <p style={styles.videoMeta}>
          {video.account_name ? `@${video.account_name}` : '—'}
          {video.file_size ? ` · ${(video.file_size / 1024 / 1024).toFixed(1)} MB` : ''}
        </p>
      </div>
      <div style={{ ...styles.statusBadge, color: s.color, background: s.bg }}>
        {s.label}
      </div>
      <div style={styles.videoTime}>
        {scheduledAt ? format(new Date(scheduledAt), 'MMM dd, HH:mm') : '—'}
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [{ data: vids }, { data: accs }] = await Promise.all([
        supabase.from('videos')
          .select('*, tiktok_accounts(username, display_name, avatar_url)')
          .order('created_at', { ascending: false }),
        supabase.from('tiktok_accounts')
          .select('id, username, display_name, avatar_url, followers, connected'),
      ]);

      const videos = (vids ?? []).map(({ tiktok_accounts, ...v }) => ({
        ...v,
        account_name: tiktok_accounts?.username ?? null,
      }));
      setVideos(videos);
      setAccounts(accs ?? []);

      const st = {
        total_views: videos.reduce((s, v) => s + (v.views ?? 0), 0),
        total_likes: videos.reduce((s, v) => s + (v.likes ?? 0), 0),
        scheduled_count: videos.filter(v => v.status === 'scheduled').length,
        published_count: videos.filter(v => v.status === 'published').length,
      };
      setStats(st);
    };
    load();
  }, []);

  const formatNum = n => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const nextVideo = videos
    .filter(v => v.status === 'scheduled' && v.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Welcome back</h1>
          <p style={styles.subtitle}>Manage your content stream from here</p>
        </div>
        <button style={styles.uploadBtn} onClick={() => onNavigate('upload')}>
          <Zap size={16} /> Upload Video
        </button>
      </div>

      <div style={styles.statsGrid}>
        <StatCard icon={Eye} label="Total Views" value={formatNum(stats?.total_views)} sub="All videos" color="var(--cyan)" />
        <StatCard icon={Heart} label="Total Likes" value={formatNum(stats?.total_likes)} sub="All videos" color="var(--pink)" />
        <StatCard icon={Clock} label="Scheduled" value={stats?.scheduled_count || '0'} sub="Pending videos" color="var(--accent)" />
        <StatCard icon={TrendingUp} label="Published" value={stats?.published_count || '0'} sub="Total published" color="var(--green)" />
      </div>

      {nextVideo && (
        <div style={styles.nextBanner}>
          <div style={styles.nextBannerLeft}>
            <div style={styles.nextBannerIcon}>
              <Calendar size={20} color="var(--accent)" />
            </div>
            <div>
              <p style={styles.nextBannerLabel}>Next Auto-Publish</p>
              <p style={styles.nextBannerTitle}>{nextVideo.title}</p>
            </div>
          </div>
          <div style={styles.nextBannerRight}>
            <p style={styles.nextBannerTime}>
              {format(new Date(nextVideo.scheduled_at), "MMM dd, yyyy 'at' HH:mm")}
            </p>
            <p style={styles.nextBannerAccount}>{nextVideo.account_name ? `@${nextVideo.account_name}` : ''}</p>
          </div>
        </div>
      )}

      <div style={styles.twoCol}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Recent Videos</h2>
            <button style={styles.seeAllBtn} onClick={() => onNavigate('queue')}>
              View all <ArrowUpRight size={14} />
            </button>
          </div>
          <div style={styles.videoList}>
            {videos.length === 0
              ? <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Henüz video yok</p>
              : videos.slice(0, 4).map(v => <VideoRow key={v.id} video={v} />)
            }
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Connected Accounts</h2>
            <button style={styles.seeAllBtn} onClick={() => onNavigate('tiktok')}>Add +</button>
          </div>
          <div style={styles.accountList}>
            {accounts.length === 0
              ? <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Hesap bağlı değil</p>
              : accounts.map(acc => (
                <div key={acc.id} style={styles.accountRow}>
                  <div style={styles.accountAvatar}>
                    {acc.avatar_url
                      ? <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : (acc.username || 'T')[0].toUpperCase()}
                  </div>
                  <div style={styles.accountInfo}>
                    <p style={styles.accountName}>@{acc.username}</p>
                    <p style={styles.accountFollowers}>{formatNum(acc.followers)} followers</p>
                  </div>
                  <div style={{
                    ...styles.connectedBadge,
                    color: acc.connected !== false ? 'var(--green)' : 'var(--text-muted)',
                    background: acc.connected !== false ? 'var(--green-light)' : 'var(--bg-card)',
                  }}>
                    {acc.connected !== false ? '● Connected' : '○ Disconnected'}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { flex: 1, padding: '32px', overflowY: 'auto', maxWidth: 1100 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  uploadBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
    boxShadow: 'var(--shadow-accent)',
  },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard: {
    padding: '18px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: 14,
  },
  statIcon: { width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statLabel: { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' },
  statSub: { fontSize: 11, color: 'var(--green)', marginTop: 2 },
  nextBanner: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, rgba(108,99,255,0.12), rgba(255,77,139,0.08))',
    border: '1px solid var(--accent-light)', borderRadius: 'var(--radius)', marginBottom: 24,
  },
  nextBannerLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  nextBannerIcon: {
    width: 42, height: 42, borderRadius: 10,
    background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  nextBannerLabel: { fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600 },
  nextBannerTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  nextBannerRight: { textAlign: 'right' },
  nextBannerTime: { fontSize: 15, fontWeight: 600, color: 'var(--accent)' },
  nextBannerAccount: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  seeAllBtn: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  videoList: { display: 'flex', flexDirection: 'column', gap: 4 },
  videoRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', borderRadius: 8, cursor: 'pointer' },
  videoThumb: {
    width: 44, height: 44, borderRadius: 8, background: 'var(--bg-secondary)',
    border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  videoInfo: { flex: 1, minWidth: 0 },
  videoTitle: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  videoMeta: { fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 },
  statusBadge: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  videoTime: { fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', minWidth: 80, textAlign: 'right' },
  accountList: { display: 'flex', flexDirection: 'column', gap: 8 },
  accountRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px', borderRadius: 8 },
  accountAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent), var(--cyan))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden',
  },
  accountInfo: { flex: 1 },
  accountName: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' },
  accountFollowers: { fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 },
  connectedBadge: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' },
};
